import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  WhatsappLogo,
  Lightning,
  ChartLineUp,
  Users,
  Moon,
  Sun,
  SignOut,
  UserCircle,
  PlusCircle,
  Trophy,
  FileText,
  Megaphone,
  Robot,
} from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

export default function StatusBar({
  source,
  lastUpdate,
  onInjectTest,
  onOpenStats,
  onOpenTeam,
  onOpenAgentStats,
  onOpenTemplates,
  onOpenCampaigns,
  onOpenBots,
  onNewChat,
}) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const live = source === "polling";
  const dotColor = "bg-[#10B981]";
  const statusText = "Live · 3s polling";

  return (
    <header
      className="flex items-center justify-between px-3 md:px-5 h-14 border-b border-black/60 bg-[#0B0F14] text-white gap-2"
      data-testid="status-bar"
    >
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <div className="h-8 w-8 rounded-md bg-[#25D366] grid place-items-center text-white shrink-0">
          <WhatsappLogo weight="fill" size={18} />
        </div>
        <div className="min-w-0">
          <div className="heading font-semibold text-[13px] md:text-[15px] leading-tight truncate text-white">
            RodBez Operation
          </div>
          <div className="text-[11px] md:text-xs text-white/60 leading-tight truncate">
            {user?.full_name || user?.email} · {user?.role}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        <div
          className="hidden lg:flex items-center gap-2 px-3 h-8 rounded-full border border-white/15 bg-white/5 text-white/80"
          data-testid="webhook-status"
        >
          <span className={`h-2 w-2 rounded-full ${dotColor} ${live ? "live-dot" : ""}`} />
          <span className="text-xs font-medium tracking-wide">{statusText}</span>
          {lastUpdate && (
            <span className="text-[11px] text-white/50">
              · {formatDistanceToNow(new Date(lastUpdate), { addSuffix: true })}
            </span>
          )}
        </div>

        <Button
          variant="default"
          size="sm"
          className="h-8 gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white px-2 md:px-3"
          onClick={onNewChat}
          data-testid="new-chat-btn"
          title="New chat"
        >
          <PlusCircle size={14} weight="fill" /> <span className="hidden md:inline">New chat</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onOpenTemplates}
          data-testid="templates-btn"
          title="WhatsApp Templates"
        >
          <FileText size={14} weight="fill" className="text-[#60A5FA]" />
          <span className="hidden md:inline">Templates</span>
        </Button>
        {user?.role === "admin" && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
            onClick={onOpenCampaigns}
            data-testid="campaigns-btn"
            title="Bulk Campaigns"
          >
            <Megaphone size={14} weight="fill" className="text-[#34D399]" />
            <span className="hidden md:inline">Campaigns</span>
          </Button>
        )}
        {user?.role === "admin" && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
            onClick={onOpenBots}
            data-testid="bots-btn"
            title="Chat Bot Builder"
          >
            <Robot size={14} weight="fill" className="text-[#A78BFA]" />
            <span className="hidden md:inline">Bots</span>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onOpenStats}
          data-testid="stats-btn"
          title="Stats"
        >
          <ChartLineUp size={14} weight="fill" /> <span className="hidden md:inline">Stats</span>
        </Button>
        {user?.role === "admin" && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
            onClick={onOpenAgentStats}
            data-testid="agent-stats-btn"
            title="Agent Performance"
          >
            <Trophy size={14} weight="fill" className="text-[#FBBF24]" />
            <span className="hidden md:inline">Agents</span>
          </Button>
        )}
        {user?.role === "admin" && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
            onClick={onOpenTeam}
            data-testid="team-btn"
            title="Team"
          >
            <Users size={14} weight="fill" /> <span className="hidden md:inline">Team</span>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="hidden sm:inline-flex h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onInjectTest}
          data-testid="inject-test-btn"
          title="Inject test message"
        >
          <Lightning size={14} weight="fill" /> <span className="hidden md:inline">Test</span>
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white" onClick={toggle} data-testid="theme-toggle">
          {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="fill" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white" data-testid="user-menu-btn">
              <UserCircle size={20} weight="fill" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5 text-xs">
              <div className="font-medium">{user?.full_name}</div>
              <div className="text-[var(--muted)]">{user?.email}</div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onInjectTest} className="sm:hidden" data-testid="logout-inject-test">
              <Lightning size={14} className="mr-2" /> Inject test
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-btn">
              <SignOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
